<template>
  <transition-group name="toast-slide" tag="div" class="toast-portal">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      class="toast-item"
      :class="'toast-' + toast.type"
      @click="dismiss(toast.id)"
    >
      <!-- Left accent bar -->
      <div class="toast-bar"></div>

      <!-- Icon -->
      <div class="toast-icon-wrap">
        <v-icon size="20" :color="iconColor(toast.type)">{{ iconName(toast.type) }}</v-icon>
      </div>

      <!-- Content -->
      <div class="toast-content">
        <div class="toast-title">{{ toastTitle(toast.type) }}</div>
        <div class="toast-message">{{ toast.message }}</div>
      </div>

      <!-- Close -->
      <button class="toast-close" @click.stop="dismiss(toast.id)">
        <v-icon size="14" color="#94A3B8">mdi-close</v-icon>
      </button>

      <!-- Progress bar -->
      <div class="toast-progress" :style="{ animationDuration: toast.timeout + 'ms' }"></div>
    </div>
  </transition-group>
</template>

<script>
import { mapState } from "vuex";

let _toastId = 0;

export default {
  name: "PremiumAlert",

  data() {
    return {
      toasts: [],
    };
  },

  computed: {
    ...mapState("alert", ["showAlert", "variant", "message", "timeout"]),
  },

  watch: {
    showAlert(val) {
      if (val && this.message) {
        this.addToast({
          message: this.message,
          variant: this.variant,
          timeout: Number(this.timeout) || 3000,
        });
      }
    },
  },

  methods: {
    addToast({ message, variant, timeout }) {
      const id = ++_toastId;
      const type = this.resolveType(variant);
      const duration = Math.max(timeout, 2000);

      this.toasts.push({ id, message, type, timeout: duration });

      setTimeout(() => this.dismiss(id), duration);
    },

    dismiss(id) {
      const idx = this.toasts.findIndex((t) => t.id === id);
      if (idx !== -1) this.toasts.splice(idx, 1);
    },

    resolveType(variant) {
      if (!variant) return "info";
      const v = variant.toLowerCase();
      if (v.includes("green") || v.includes("success")) return "success";
      if (v.includes("red") || v.includes("error") || v.includes("danger")) return "error";
      if (v.includes("amber") || v.includes("warning") || v.includes("orange")) return "warning";
      if (v.includes("blue") || v.includes("info")) return "info";
      return "info";
    },

    iconName(type) {
      const map = {
        success: "mdi-check-circle-outline",
        error:   "mdi-alert-circle-outline",
        warning: "mdi-alert-outline",
        info:    "mdi-information-outline",
      };
      return map[type] || "mdi-information-outline";
    },

    iconColor(type) {
      const map = {
        success: "#10B981",
        error:   "#EF4444",
        warning: "#F59E0B",
        info:    "#3B82F6",
      };
      return map[type] || "#3B82F6";
    },

    toastTitle(type) {
      const map = {
        success: "Success",
        error:   "Error",
        warning: "Warning",
        info:    "Info",
      };
      return map[type] || "Notice";
    },
  },
};
</script>

<style scoped>
/* ── Portal ── */
.toast-portal {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
  max-width: 360px;
  width: calc(100vw - 40px);
}

/* ── Toast Card ── */
.toast-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #ffffff;
  border-radius: 14px;
  padding: 14px 14px 18px 0;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.07),
    0 10px 24px -4px rgba(0, 0, 0, 0.10),
    0 0 0 1px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.2s, box-shadow 0.2s;
}
.toast-item:hover {
  transform: translateY(-2px);
  box-shadow:
    0 8px 16px -2px rgba(0, 0, 0, 0.10),
    0 20px 40px -8px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(0, 0, 0, 0.04);
}

/* Dark mode */
.theme--dark .toast-item {
  background: #1E293B;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.3),
    0 10px 24px -4px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.06);
}

/* ── Left accent bar ── */
.toast-bar {
  width: 4px;
  align-self: stretch;
  border-radius: 0 4px 4px 0;
  flex-shrink: 0;
  margin-left: 0;
}
.toast-success .toast-bar { background: #10B981; }
.toast-error   .toast-bar { background: #EF4444; }
.toast-warning .toast-bar { background: #F59E0B; }
.toast-info    .toast-bar { background: #3B82F6; }

/* ── Icon ── */
.toast-icon-wrap {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.toast-success .toast-icon-wrap { background: rgba(16, 185, 129, 0.10); }
.toast-error   .toast-icon-wrap { background: rgba(239, 68, 68, 0.10); }
.toast-warning .toast-icon-wrap { background: rgba(245, 158, 11, 0.10); }
.toast-info    .toast-icon-wrap { background: rgba(59, 130, 246, 0.10); }

/* ── Content ── */
.toast-content {
  flex: 1;
  min-width: 0;
  padding-top: 1px;
}
.toast-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 3px;
  letter-spacing: -0.01em;
}
.theme--dark .toast-title { color: #F1F5F9; }

.toast-message {
  font-size: 12.5px;
  font-weight: 500;
  color: #6B7280;
  line-height: 1.5;
  word-break: break-word;
}
.theme--dark .toast-message { color: #94A3B8; }

/* ── Close ── */
.toast-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  margin-top: 1px;
}
.toast-close:hover {
  background: #F1F5F9;
}
.theme--dark .toast-close:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* ── Progress bar ── */
.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  border-radius: 0 0 14px 14px;
  animation: toastProgress linear forwards;
  transform-origin: left;
}
.toast-success .toast-progress { background: linear-gradient(90deg, #10B981, #34D399); }
.toast-error   .toast-progress { background: linear-gradient(90deg, #EF4444, #F87171); }
.toast-warning .toast-progress { background: linear-gradient(90deg, #F59E0B, #FCD34D); }
.toast-info    .toast-progress { background: linear-gradient(90deg, #3B82F6, #60A5FA); }

@keyframes toastProgress {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}

/* ── Transitions ── */
.toast-slide-enter-active {
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.toast-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 1, 1);
}
.toast-slide-enter {
  opacity: 0;
  transform: translateX(60px) scale(0.92);
}
.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(60px) scale(0.92);
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .toast-portal {
    top: 12px;
    right: 12px;
    left: 12px;
    max-width: 100%;
    width: auto;
  }
}
</style>
